import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Trash2, Copy, Pencil, ImagePlus } from "lucide-react";
import { api, fileUrl, errText } from "../lib/api";

const DAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];
const TABS = ["Auto-Replies", "Business Hours", "Quick Replies", "Agents", "Incentives", "Widget"];

export default function Settings() {
  const [tab, setTab] = useState("Auto-Replies");
  const [s, setS] = useState(null);
  const [qrs, setQrs] = useState([]);
  const [agents, setAgents] = useState([]);
  const [newQr, setNewQr] = useState({ shortcut: "", text: "", attachment: null });
  const [editingQr, setEditingQr] = useState(null);
  const [qrUploading, setQrUploading] = useState(false);
  const qrFileRef = useRef(null);
  const [newAgent, setNewAgent] = useState({ name: "", email: "", password: "", role: "agent" });
  const [holidayInput, setHolidayInput] = useState("");

  const load = () => {
    api.get("/settings").then((r) => setS(r.data));
    api.get("/quick-replies").then((r) => setQrs(r.data));
    api.get("/agents").then((r) => setAgents(r.data));
  };
  useEffect(load, []);

  const save = async (patch) => {
    try {
      const { data } = await api.put("/settings", patch);
      setS(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(errText(e)); }
  };

  const resetQrForm = () => { setEditingQr(null); setNewQr({ shortcut: "", text: "", attachment: null }); };

  const uploadQrFile = async (f) => {
    if (!f) return;
    setQrUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      setNewQr((p) => ({ ...p, attachment: { url: data.url, name: data.name, content_type: data.content_type, size: data.size, kind: data.kind } }));
    } catch (e) { toast.error(errText(e)); }
    setQrUploading(false);
  };

  const saveQr = async () => {
    if (!newQr.shortcut || (!newQr.text && !newQr.attachment)) { toast.error("Shortcut plus text or an image is required"); return; }
    try {
      if (editingQr) await api.put(`/quick-replies/${editingQr.id}`, newQr);
      else await api.post("/quick-replies", newQr);
      resetQrForm(); load(); toast.success(editingQr ? "Quick reply updated" : "Quick reply added");
    } catch (e) { toast.error(errText(e)); }
  };

  if (!s) return <div className="p-8 font-heading font-bold">Loading…</div>;

  const widgetOrigin = window.location.origin;
  const snippet = `<script src="${widgetOrigin}/widget.js"></script>`;
  const customSnippet = `<script src="${widgetOrigin}/widget.js" data-mode="custom" data-trigger=".open-support-chat"></script>`;

  return (
    <div className="p-6 overflow-y-auto h-full" data-testid="settings-page">
      <h2 className="font-heading font-extrabold text-2xl">Settings</h2>
      <p className="font-script text-xl text-amber-800 mb-4">tune your desk ✦</p>
      <div className="flex gap-1.5 mb-5 flex-wrap">
        {TABS.map((t) => <button key={t} data-testid={`settings-tab-${t.toLowerCase().replace(/[ -]/g, "")}`} className={`filter-pill ${tab === t ? "active" : ""}`} onClick={() => setTab(t)}>{t}</button>)}
      </div>

      {tab === "Auto-Replies" && (
        <div className="max-w-2xl space-y-4">
          <div className="card-sticky p-4">
            <label className="sd-label">Auto-assignment (round-robin)</label>
            <div className="flex items-center gap-3 mt-2">
              <button data-testid="auto-assign-toggle" onClick={() => save({ auto_assign: !s.auto_assign })}
                className={`w-12 h-6 rounded-full border-2 border-[#131E3A] relative transition-colors ${s.auto_assign ? "bg-[#D4F0F0]" : "bg-white"}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-[#131E3A] transition-all ${s.auto_assign ? "left-6" : "left-0.5"}`} />
              </button>
              <span className="text-sm font-semibold">{s.auto_assign ? "New chats auto-assigned to active agents" : "Manual assignment only"}</span>
            </div>
          </div>
          <div className="card-tactile p-4">
            <label className="sd-label">Welcome message</label>
            <textarea data-testid="welcome-message-input" className="sd-input mt-1 min-h-[70px] text-sm" defaultValue={s.welcome_message} onBlur={(e) => e.target.value !== s.welcome_message && save({ welcome_message: e.target.value })} />
          </div>
          <div className="card-tactile p-4">
            <label className="sd-label">Away message (outside business hours)</label>
            <textarea data-testid="away-message-input" className="sd-input mt-1 min-h-[70px] text-sm" defaultValue={s.away_message} onBlur={(e) => e.target.value !== s.away_message && save({ away_message: e.target.value })} />
          </div>
          <div className="card-tactile p-4">
            <label className="sd-label">Ticket closing remarks (one per line)</label>
            <textarea data-testid="preset-remarks-input" className="sd-input mt-1 min-h-[100px] text-sm" defaultValue={(s.preset_remarks || []).join("\n")} onBlur={(e) => save({ preset_remarks: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })} />
          </div>
        </div>
      )}

      {tab === "Business Hours" && (
        <div className="max-w-2xl space-y-4">
          <div className="card-tactile p-4 space-y-2">
            <p className="sd-label">Weekly hours (Asia/Kolkata)</p>
            {DAYS.map(([d, label]) => {
              const h = s.business_hours[d];
              return (
                <div key={d} className="flex items-center gap-3 text-sm">
                  <input data-testid={`hours-enabled-${d}`} type="checkbox" checked={h.enabled} onChange={(e) => save({ business_hours: { ...s.business_hours, [d]: { ...h, enabled: e.target.checked } } })} className="w-4 h-4 accent-[#131E3A]" />
                  <span className="w-24 font-semibold">{label}</span>
                  <input data-testid={`hours-start-${d}`} type="time" className="sd-input w-28 py-1" value={h.start} disabled={!h.enabled} onChange={(e) => save({ business_hours: { ...s.business_hours, [d]: { ...h, start: e.target.value } } })} />
                  <span>–</span>
                  <input data-testid={`hours-end-${d}`} type="time" className="sd-input w-28 py-1" value={h.end} disabled={!h.enabled} onChange={(e) => save({ business_hours: { ...s.business_hours, [d]: { ...h, end: e.target.value } } })} />
                </div>
              );
            })}
          </div>
          <div className="card-sticky p-4">
            <p className="sd-label mb-2">Holidays</p>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {(s.holidays || []).map((h) => (
                <span key={h} className="text-xs font-bold bg-white border border-[#131E3A] rounded-full px-2 py-0.5">
                  {h} <button data-testid={`remove-holiday-${h}`} onClick={() => save({ holidays: s.holidays.filter((x) => x !== h) })}>×</button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input data-testid="holiday-input" type="date" className="sd-input w-44 py-1.5" value={holidayInput} onChange={(e) => setHolidayInput(e.target.value)} />
              <button data-testid="add-holiday-button" className="btn-tactile px-3 text-sm" onClick={() => { if (holidayInput) { save({ holidays: [...(s.holidays || []), holidayInput] }); setHolidayInput(""); } }}>Add</button>
            </div>
          </div>
        </div>
      )}

      {tab === "Quick Replies" && (
        <div className="max-w-2xl space-y-3">
          <div className="card-sticky p-4">
            <p className="sd-label mb-2">{editingQr ? `Edit quick reply /${editingQr.shortcut}` : "Add quick reply"}</p>
            <div className="flex gap-2 mb-2">
              <input data-testid="qr-shortcut-input" className="sd-input" style={{ flex: "0 0 9rem" }} placeholder="shortcut" value={newQr.shortcut} onChange={(e) => setNewQr({ ...newQr, shortcut: e.target.value.replace(/\s/g, "") })} />
              <input data-testid="qr-text-input" className="sd-input flex-1" placeholder="Reply text…" value={newQr.text} onChange={(e) => setNewQr({ ...newQr, text: e.target.value })} />
            </div>
            <div className="flex items-center gap-3 mb-3">
              <button type="button" data-testid="qr-image-button" className="btn-outline-tactile px-3 py-1.5 text-xs flex items-center gap-1.5" onClick={() => qrFileRef.current?.click()} disabled={qrUploading}>
                <ImagePlus size={13} /> {qrUploading ? "Uploading…" : "Attach image / video"}
              </button>
              <input ref={qrFileRef} type="file" accept="image/*,video/*" hidden onChange={(e) => { uploadQrFile(e.target.files?.[0]); e.target.value = ""; }} />
              {newQr.attachment && (
                <div className="flex items-center gap-1.5" data-testid="qr-attachment-preview">
                  {newQr.attachment.kind === "image" ? <img src={fileUrl(newQr.attachment.url)} alt="" className="w-10 h-10 rounded object-cover border-2 border-[#131E3A]" /> : <span className="text-xs font-semibold">📎 {newQr.attachment.name}</span>}
                  <button data-testid="qr-remove-attachment" onClick={() => setNewQr({ ...newQr, attachment: null })} className="text-red-600 font-bold">×</button>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button data-testid="qr-add-button" className="btn-tactile px-4 py-1.5 text-sm" onClick={saveQr}>{editingQr ? "Save changes" : "Add"}</button>
              {editingQr && <button data-testid="qr-cancel-edit" className="btn-outline-tactile px-4 py-1.5 text-sm" onClick={resetQrForm}>Cancel</button>}
            </div>
          </div>
          {qrs.map((q) => (
            <div key={q.id} className="card-tactile p-3 flex items-center gap-3">
              <span className="font-heading font-bold text-sm bg-[#D0E8FF] border border-[#131E3A] rounded px-1.5 shrink-0">/{q.shortcut}</span>
              {q.attachment && q.attachment.kind === "image" && <img src={fileUrl(q.attachment.url)} alt="" className="w-12 h-12 rounded-lg object-cover border-2 border-[#131E3A] shrink-0" />}
              <p className="flex-1 text-sm min-w-0">{q.text}{q.attachment && q.attachment.kind !== "image" && <span className="block text-xs text-slate-500">📎 {q.attachment.name}</span>}</p>
              <button data-testid={`qr-edit-${q.shortcut}`} onClick={() => { setEditingQr(q); setNewQr({ shortcut: q.shortcut, text: q.text || "", attachment: q.attachment || null }); }} className="text-slate-600 hover:text-[#131E3A]"><Pencil size={15} /></button>
              <button data-testid={`qr-delete-${q.shortcut}`} onClick={async () => { await api.delete(`/quick-replies/${q.id}`); if (editingQr?.id === q.id) resetQrForm(); load(); }} className="text-red-600"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}

      {tab === "Agents" && (
        <div className="max-w-2xl space-y-3">
          <div className="card-sticky p-4">
            <p className="sd-label mb-2">Add agent</p>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <input data-testid="agent-name-input" className="sd-input" placeholder="Name" value={newAgent.name} onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })} />
              <input data-testid="agent-email-input" className="sd-input" placeholder="Email" value={newAgent.email} onChange={(e) => setNewAgent({ ...newAgent, email: e.target.value })} />
              <input data-testid="agent-password-input" className="sd-input" placeholder="Password" type="password" value={newAgent.password} onChange={(e) => setNewAgent({ ...newAgent, password: e.target.value })} />
              <select data-testid="agent-role-select" className="sd-input" value={newAgent.role} onChange={(e) => setNewAgent({ ...newAgent, role: e.target.value })}>
                <option value="agent">Agent</option><option value="admin">Admin</option>
              </select>
            </div>
            <button data-testid="agent-add-button" className="btn-tactile px-4 py-1.5 text-sm" onClick={async () => {
              try { await api.post("/agents", newAgent); setNewAgent({ name: "", email: "", password: "", role: "agent" }); load(); toast.success("Agent added"); }
              catch (e) { toast.error(errText(e)); }
            }}>Add agent</button>
          </div>
          {agents.map((a) => (
            <div key={a.id} data-testid={`agent-row-${a.email}`} className="card-tactile p-3 flex items-center gap-3 text-sm">
              <span title={a.availability === "offline" ? "On break" : a.online ? "Online" : "Away"}>{a.availability === "offline" ? "⏸" : <span className={a.online ? "text-green-700" : "text-slate-400"}>●</span>}</span>
              <div className="flex-1"><span className="font-bold">{a.name}</span> <span className="text-slate-500 text-xs">{a.email}</span></div>
              <span className="uppercase text-[10px] font-bold tracking-wider bg-[#FDF0A0] border border-[#131E3A] rounded px-1.5 py-0.5">{a.role}</span>
              <button data-testid={`agent-toggle-${a.email}`} onClick={async () => { await api.patch(`/agents/${a.id}`, { is_active: !a.is_active }); load(); }}
                className={`filter-pill ${a.is_active ? "active" : ""}`}>{a.is_active ? "Active" : "Inactive"}</button>
            </div>
          ))}
        </div>
      )}

      {tab === "Incentives" && (
        <div className="max-w-md space-y-4">
          <div className="card-sticky p-4">
            <p className="sd-label">Brownie points per star rating</p>
            <p className="font-script text-lg text-amber-800 mb-3">reward great service, flag the poor ones ✦</p>
            {[5, 4, 3, 2, 1].map((star) => (
              <div key={star} className="flex items-center gap-3 mb-2">
                <span className="w-24 text-amber-500 font-bold tracking-tight">{"★".repeat(star)}</span>
                <input data-testid={`brownie-input-${star}`} type="number" className="sd-input py-1.5" style={{ flex: "0 0 7rem" }}
                  value={s.brownie_points?.[String(star)] ?? 0}
                  onChange={(e) => setS({ ...s, brownie_points: { ...s.brownie_points, [String(star)]: e.target.value } })}
                  onBlur={() => save({ brownie_points: Object.fromEntries(Object.entries(s.brownie_points || {}).map(([k, v]) => [k, parseInt(v) || 0])) })} />
                <span className="text-xs text-slate-600">points</span>
              </div>
            ))}
            <p className="text-xs text-slate-600 mt-2">Use negative values to penalize low ratings. Totals appear per agent in the Dashboard and in each agent's own sidebar.</p>
          </div>
        </div>
      )}

      {tab === "Widget" && (
        <div className="max-w-2xl space-y-4">
          <div className="card-tactile p-4 space-y-3">
            <p className="sd-label">Widget branding</p>
            <div className="flex items-center gap-3">
              <label className="text-sm font-semibold w-32">Accent color</label>
              <input data-testid="widget-color-input" type="color" value={s.widget_color || "#2563EB"} onChange={(e) => save({ widget_color: e.target.value })} className="w-14 h-9 border-2 border-[#131E3A] rounded cursor-pointer" />
              <code className="text-xs">{s.widget_color}</code>
            </div>
            <div>
              <label className="sd-label">Header title</label>
              <input data-testid="widget-title-input" className="sd-input mt-1" defaultValue={s.widget_title} onBlur={(e) => e.target.value !== s.widget_title && save({ widget_title: e.target.value })} />
            </div>
            <div>
              <label className="sd-label">Greeting text (pre-chat form)</label>
              <textarea data-testid="widget-greeting-input" className="sd-input mt-1 text-sm min-h-[60px]" defaultValue={s.widget_greeting} onBlur={(e) => e.target.value !== s.widget_greeting && save({ widget_greeting: e.target.value })} />
            </div>
          </div>
          <div className="card-sticky p-4">
            <p className="sd-label mb-2">Embed snippet — paste before &lt;/body&gt; on staranddaisy.in</p>
            <div className="flex gap-2 items-center">
              <code data-testid="widget-snippet" className="flex-1 bg-white border-2 border-[#131E3A] rounded-lg p-3 text-xs break-all">{snippet}</code>
              <button data-testid="copy-snippet-button" className="btn-tactile p-2" onClick={() => { navigator.clipboard.writeText(snippet); toast.success("Copied!"); }}><Copy size={15} /></button>
            </div>
            <p className="text-xs text-slate-600 mt-2">Color, title & greeting above apply automatically — no snippet change needed. The widget opens the standalone customer page at <a className="underline" href="/chat" target="_blank" rel="noreferrer">/chat</a> inside an iframe.</p>
          </div>
          <div className="card-tactile p-4">
            <p className="sd-label mb-2">Custom trigger — no floating bubble</p>
            <div className="flex gap-2 items-center">
              <code data-testid="widget-custom-snippet" className="flex-1 bg-white border-2 border-[#131E3A] rounded-lg p-3 text-xs break-all">{customSnippet}</code>
              <button data-testid="copy-custom-snippet-button" className="btn-tactile p-2" onClick={() => { navigator.clipboard.writeText(customSnippet); toast.success("Copied!"); }}><Copy size={15} /></button>
            </div>
            <p className="text-xs text-slate-600 mt-2">
              <b>data-mode="custom"</b> hides the chat bubble entirely. The chat window opens when any element matching <b>data-trigger</b> (a CSS selector — e.g. add class <code>open-support-chat</code> to your own "Chat with us" / "Start Chat Now" button) is clicked. Works for buttons added later too.<br />
              You can also open it from your site's JavaScript: <code>window.SNDChat.open()</code>, <code>.close()</code>, <code>.toggle()</code>, <code>.isOpen()</code>. New-message counts fire a <code>snd:unread</code> event on <code>window</code> so you can show your own badge.
            </p>
          </div>
          <div className="card-tactile p-4">
            <p className="sd-label mb-2">Live preview</p>
            <iframe key={`${s.widget_color}-${s.widget_title}-${s.widget_greeting}`} title="widget preview" src="/chat?embed=1" className="w-[380px] h-[560px] border-2 border-[#131E3A] rounded-2xl" />
          </div>
        </div>
      )}
    </div>
  );
}
